// IndexedDB utility for offline data storage
const DB_NAME = 'NucleusOffline';
const DB_VERSION = 1;

let dbInstance = null;

export const initDB = () => {
  return new Promise((resolve, reject) => {
    if (dbInstance) {
      resolve(dbInstance);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('IndexedDB error:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      dbInstance = request.result;
      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Pending attendance records
      if (!db.objectStoreNames.contains('pending_attendance')) {
        const attendanceStore = db.createObjectStore('pending_attendance', { 
          keyPath: 'id', 
          autoIncrement: true 
        });
        attendanceStore.createIndex('class_id', 'class_id', { unique: false });
        attendanceStore.createIndex('date', 'date', { unique: false });
        attendanceStore.createIndex('sync_status', 'sync_status', { unique: false });
      }

      // Cached user data
      if (!db.objectStoreNames.contains('cached_data')) {
        const dataStore = db.createObjectStore('cached_data', { keyPath: 'key' });
        dataStore.createIndex('expires_at', 'expires_at', { unique: false });
      }

      // Downloaded lessons for offline viewing
      if (!db.objectStoreNames.contains('offline_lessons')) {
        const lessonsStore = db.createObjectStore('offline_lessons', { keyPath: 'lesson_id' });
        lessonsStore.createIndex('course_id', 'course_id', { unique: false });
        lessonsStore.createIndex('downloaded_at', 'downloaded_at', { unique: false });
      }

      // Notification preferences
      if (!db.objectStoreNames.contains('preferences')) {
        db.createObjectStore('preferences', { keyPath: 'key' });
      }
    };
  });
};

// Generic CRUD operations
export const addToStore = async (storeName, data) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    const request = store.add(data);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

export const putToStore = async (storeName, data) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    const request = store.put(data);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

export const getFromStore = async (storeName, key) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    const request = store.get(key);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

export const getAllFromStore = async (storeName) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    const request = store.getAll();
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

export const deleteFromStore = async (storeName, key) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    const request = store.delete(key);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

export const clearStore = async (storeName) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    const request = store.clear();
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

// Attendance-specific functions
export const savePendingAttendance = async (classId, date, attendanceData, token) => {
  return addToStore('pending_attendance', {
    class_id: classId,
    date: date,
    data: attendanceData,
    token: token,
    created_at: new Date().toISOString(),
    sync_status: 'pending'
  });
};

export const getPendingAttendance = async () => {
  return getAllFromStore('pending_attendance');
};

export const markAttendanceSynced = async (id) => {
  return deleteFromStore('pending_attendance', id);
};

// Lesson download functions
export const saveOfflineLesson = async (lesson) => {
  return putToStore('offline_lessons', {
    lesson_id: lesson.id,
    course_id: lesson.course_id,
    unit_id: lesson.unit_id,
    title: lesson.title,
    content: lesson.content,
    resources: lesson.resources || [],
    downloaded_at: new Date().toISOString(),
    expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() // 7 days
  });
};

export const getOfflineLesson = async (lessonId) => {
  return getFromStore('offline_lessons', lessonId);
};

export const getOfflineLessons = async () => {
  return getAllFromStore('offline_lessons');
};

export const removeOfflineLesson = async (lessonId) => {
  return deleteFromStore('offline_lessons', lessonId);
};

export const clearExpiredLessons = async () => {
  const lessons = await getAllFromStore('offline_lessons');
  const now = new Date().toISOString();
  
  for (const lesson of lessons) {
    if (lesson.expires_at < now) {
      await deleteFromStore('offline_lessons', lesson.lesson_id);
    }
  }
};

// Cache data with expiration
export const cacheData = async (key, data, ttlMinutes = 60) => {
  return putToStore('cached_data', {
    key: key,
    data: data,
    cached_at: new Date().toISOString(),
    expires_at: new Date(Date.now() + ttlMinutes * 60 * 1000).toISOString()
  });
};

export const getCachedData = async (key) => {
  const cached = await getFromStore('cached_data', key);
  if (!cached) return null;
  
  if (new Date(cached.expires_at) < new Date()) {
    await deleteFromStore('cached_data', key);
    return null;
  }
  
  return cached.data;
};

// Preferences
export const setPreference = async (key, value) => {
  return putToStore('preferences', { key, value });
};

export const getPreference = async (key) => {
  const pref = await getFromStore('preferences', key);
  return pref ? pref.value : null;
};

// Calculate storage usage
export const getStorageUsage = async () => {
  if ('storage' in navigator && 'estimate' in navigator.storage) {
    const estimate = await navigator.storage.estimate();
    return {
      usage: estimate.usage || 0,
      quota: estimate.quota || 0,
      percentUsed: estimate.quota ? ((estimate.usage / estimate.quota) * 100).toFixed(2) : 0
    };
  }
  return { usage: 0, quota: 0, percentUsed: 0 };
};
